/**
 * exerciseSelectionEngine.js — Moteur de sélection déterministe (aucune IA) des exercices
 * d'une séance à partir d'une durée CIBLE (minutes), pour respecter à 100% ce que le client a
 * demandé dans le questionnaire (± tolérance affichée), au lieu d'un nombre fixe d'exercices
 * par groupe musculaire qui ignorait complètement la durée souhaitée (bug rapporté 12/09/2026 :
 * 45 min demandées → seulement 24 min obtenues avec 2 exercices).
 *
 * Algorithme (transparent, pas de boîte noire) :
 *  1. Garantit une couverture minimale : 1 exercice par groupe musculaire sélectionné.
 *  2. Si trop court : ajoute des exercices en tournant sur les groupes (équilibré), jusqu'à
 *     rentrer dans la tolérance — jamais 2x le même exercice.
 *  3. Si le stock d'exercices dispo (matériel/groupe) est épuisé et toujours trop court :
 *     augmente le nombre de séries des exercices existants (jusqu'à un maximum raisonnable).
 *  4. Si trop long : retire d'abord les exercices en double dans un même groupe (jamais le
 *     dernier restant d'un groupe), puis réduit les séries en dernier recours.
 * Toujours transparent sur le résultat final (estimatedMinutes) — jamais de promesse non tenue.
 */
import { estimateSessionSeconds } from './sessionTimeEstimator';
import { GOAL_PARAMS } from './goalParams';

export const TOLERANCE_MINUTES = 5;
const MIN_SETS = 2;
const MAX_SETS = 5;
const MAX_EXERCISES_PER_GROUP = 4;
const MAX_GUARD_ITERATIONS = 80;

const isTimeBasedName = (name) => /gainage|planche|corde à sauter|assault bike|rameur|mountain climber/i.test(name || '');

const nameOf = (libEx) => libEx.name_fr || libEx.name;

const toExercise = (libEx, params) => {
  const name = nameOf(libEx);
  const timeBased = libEx.type === 'cardio' || isTimeBasedName(name);
  return {
    _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    exercise_id: libEx.id, name, muscle_group: libEx.muscle_group, equipment: libEx.equipment,
    set_type: 'straight', group_id: null,
    sets_count: params.sets, reps_scheme: timeBased ? '30-45s' : params.reps, is_time_based: timeBased,
    rest_seconds: libEx.rest_default_sec || params.rest, tempo: libEx.tempo_default || '',
    image_url: libEx.image_url, video_url: libEx.video_status === 'ready' ? libEx.video_url : null,
    video_status: libEx.video_status, video_disabled: libEx.video_disabled,
    execution: libEx.execution, tips: libEx.tips,
  };
};

const totalSeconds = (exercises) => estimateSessionSeconds({ exercises });

/**
 * @param {string[]} groups - groupes musculaires demandés pour CETTE séance
 * @param {string[]} equipment - matériel autorisé (slugs de EQUIPMENT_TYPES)
 * @param {number} targetMinutes - durée cible de la séance (± TOLERANCE_MINUTES)
 * @param {string} goal - objectif (clé de GOAL_PARAMS)
 * @param {object[]} library - pool d'exercices dispo (déjà filtré par groupe si besoin, sinon filtré ici)
 * @param {string[]} excludedGroups - groupes à éviter entièrement (blessures)
 * @param {Set<string>} usedNamesGlobal - noms déjà utilisés ailleurs dans le programme (évite les doublons cross-séances)
 * @returns {{ exercises: object[], estimatedMinutes: number, targetMinutes: number, coveredGroups: string[], excludedGroups: string[], insufficientPool: boolean }}
 */
export const buildSessionForTarget = ({
  groups, equipment, targetMinutes, goal, library, excludedGroups = [], usedNamesGlobal = new Set(),
}) => {
  const params = GOAL_PARAMS[goal] || { sets: 3, reps: '10-12', rest: 60 };
  const activeGroups = (groups || []).filter((g) => !excludedGroups.includes(g));
  if (!activeGroups.length || !targetMinutes) {
    return { exercises: [], estimatedMinutes: 0, targetMinutes: targetMinutes || 0, coveredGroups: [], excludedGroups, insufficientPool: false };
  }

  const poolByGroup = {};
  activeGroups.forEach((g) => {
    poolByGroup[g] = (library || []).filter((e) => e.muscle_group === g && equipment.includes(e.equipment));
  });

  const usedNames = new Set(usedNamesGlobal);
  const exercises = [];
  const nextUnusedFrom = (group) => poolByGroup[group]?.find((e) => !usedNames.has(nameOf(e)));

  // Étape 1 — couverture minimale garantie (1 exercice/groupe sélectionné).
  activeGroups.forEach((g) => {
    const pick = nextUnusedFrom(g);
    if (pick) { exercises.push(toExercise(pick, params)); usedNames.add(nameOf(pick)); }
  });

  const targetSeconds = targetMinutes * 60;
  const toleranceSeconds = TOLERANCE_MINUTES * 60;

  // Étape 2 — ajoute des exercices en tournant sur les groupes tant que c'est trop court.
  let guard = 0;
  while (totalSeconds(exercises) < targetSeconds - toleranceSeconds && guard < MAX_GUARD_ITERATIONS) {
    let added = false;
    for (const g of activeGroups) {
      if (exercises.filter((e) => e.muscle_group === g).length >= MAX_EXERCISES_PER_GROUP) continue;
      const pick = nextUnusedFrom(g);
      if (!pick) continue;
      exercises.push(toExercise(pick, params));
      usedNames.add(nameOf(pick));
      added = true;
      if (totalSeconds(exercises) >= targetSeconds - toleranceSeconds) break;
    }
    guard += 1;
    if (!added) break;
  }

  // Étape 3 — pool épuisé mais toujours trop court : augmente les séries existantes.
  guard = 0;
  while (totalSeconds(exercises) < targetSeconds - toleranceSeconds && guard < 40) {
    const candidates = exercises.filter((e) => e.sets_count < MAX_SETS).sort((a, b) => a.sets_count - b.sets_count);
    if (!candidates.length) break;
    candidates[0].sets_count += 1;
    guard += 1;
  }

  // Étape 4 — trop long : retire les doublons de groupe (jamais le dernier), puis réduit les séries.
  guard = 0;
  while (totalSeconds(exercises) > targetSeconds + toleranceSeconds && guard < MAX_GUARD_ITERATIONS) {
    const groupCounts = {};
    exercises.forEach((e) => { groupCounts[e.muscle_group] = (groupCounts[e.muscle_group] || 0) + 1; });
    let removeIdx = -1;
    for (let i = exercises.length - 1; i >= 0; i -= 1) {
      if (groupCounts[exercises[i].muscle_group] > 1) { removeIdx = i; break; }
    }
    if (removeIdx >= 0) {
      exercises.splice(removeIdx, 1);
    } else {
      const shrinkable = exercises.filter((e) => e.sets_count > MIN_SETS).sort((a, b) => b.sets_count - a.sets_count);
      if (!shrinkable.length) break;
      shrinkable[0].sets_count -= 1;
    }
    guard += 1;
  }

  return {
    exercises,
    estimatedMinutes: Math.round(totalSeconds(exercises) / 60),
    targetMinutes,
    coveredGroups: [...new Set(exercises.map((e) => e.muscle_group))],
    excludedGroups,
    // Ré-évalué en sortie, APRÈS l'étape 3 (augmentation des séries) — sinon le flag était
    // parfois positionné à `true` en sortie d'étape 2 (pool épuisé) alors que l'étape 3
    // ramenait déjà le résultat dans la tolérance, ce qui déclenchait un toast "pas assez
    // d'exercices" trompeur alors que le résultat final était correct (bug remonté par le
    // testing_agent 12/09/2026).
    insufficientPool: totalSeconds(exercises) < targetSeconds - toleranceSeconds,
  };
};
